import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import ClientDashboard from "@/pages/ClientDashboard";
import AddProduct from "@/pages/AddProduct";
import PresentationDetail from "@/pages/PresentationDetail";
import PresentationForm from "@/pages/PresentationForm";
import Projects from "@/pages/Projects";
import ProjectForm from "@/pages/ProjectForm";
import ProjectDetail from "@/pages/ProjectDetail";
import SpaceDetail from "@/pages/SpaceDetail";
import ProductDetail from "@/pages/ProductDetail";
import InvoiceBuilder from "@/pages/InvoiceBuilder";
import InvoiceLibrary from "@/pages/InvoiceLibrary";
import Procurement from "@/pages/Procurement";
import ProcurementDetail from "@/pages/ProcurementDetail";
import Library from "@/pages/Library";
import { Vendors, VendorDetail } from "@/pages/Vendors";
import { Clients, ClientDetail } from "@/pages/Clients";
import { FilesPage, Settings } from "@/pages/Misc";
import Messages from "@/pages/Messages";
import Decisions from "@/pages/Decisions";
import ScenarioDetail, { ScenarioCompare } from "@/pages/ScenarioDetail";
import OptionSet from "@/pages/OptionSet";
import CalendarPage from "@/pages/Calendar";
import Invoices from "@/pages/Invoices";
import InvoiceDetail from "@/pages/InvoiceDetail";
import PortalAccess from "@/pages/PortalAccess";
import ClientPreview from "@/pages/ClientPreview";
import SsoLanding from "@/pages/SsoLanding";
import ConvertFromWebsite from "@/pages/ConvertFromWebsite";
import PublicInvoice from "@/pages/PublicInvoice";
import Inquiries from "@/pages/Inquiries";
import ContentStudio from "@/pages/ContentStudio";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center text-sm text-fog">Loading Asthetik OS…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Shell>{children}</Shell>;
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center text-sm text-fog">Loading…</div>;
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

function HomeRoute() {
  const { user } = useAuth();
  return user?.role === "client" ? <ClientDashboard /> : <Dashboard />;
}

const routes = [
  ["/", <HomeRoute />],
  ["/projects", <Projects />],
  ["/inquiries", <Inquiries />],
  ["/content", <ContentStudio />],
  ["/projects/new", <ProjectForm />],
  ["/projects/:id", <ProjectDetail />],
  ["/projects/:id/invoice/new", <InvoiceBuilder />],
  ["/invoice-library", <InvoiceLibrary />],
  ["/projects/:id/edit", <ProjectForm />],
  ["/spaces/:id", <SpaceDetail />],
  ["/presentations/new", <PresentationForm />],
  ["/presentations/:id", <PresentationDetail />],
  ["/presentations/:id/edit", <PresentationForm />],
  ["/products/new", <AddProduct />],
  ["/products/manual", <ProductDetail />],
  ["/products/:id", <ProductDetail />],
  ["/procurement", <Procurement />],
  ["/procurement/:id", <ProcurementDetail />],
  ["/library", <Library />],
  ["/vendors", <Vendors />],
  ["/vendors/:id", <VendorDetail />],
  ["/clients", <Clients />],
  ["/clients/:id", <ClientDetail />],
  ["/messages", <Messages />],
  ["/decisions", <Decisions />],
  ["/options/:id", <OptionSet />],
  ["/calendar", <CalendarPage />],
  ["/invoices", <Invoices />],
  ["/invoices/:id", <InvoiceDetail />],
  ["/scenarios/compare", <ScenarioCompare />],
  ["/scenarios/:id", <ScenarioDetail />],
  ["/files", <FilesPage />],
  ["/settings", <Settings />],
];

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" />
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/portal/:token" element={<PortalAccess />} />
            <Route path="/i/:token" element={<PublicInvoice />} />
            <Route path="/client-preview/:token" element={<ClientPreview />} />
            <Route path="/sso" element={<SsoLanding />} />
            <Route path="/inquiries/:inquiryId/convert" element={<ConvertFromWebsite />} />
            {routes.map(([path, element]) => (
              <Route key={path} path={path} element={<Protected>{element}</Protected>} />
            ))}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
